// src/hooks/useLocationHandling.ts
import { useLocationContext } from '../../../shared/contexts/useLocationContext';
import api from '../../../shared/api/axios';

type AppLocation = {
  city: string;
  country: string;
  countryCode?: string;
};

type TemporaryMapLocation = AppLocation & {
  coords?: {
    lat: number;
    lng: number;
  };
};

async function getLocationConsent(): Promise<boolean> {
  const cached = localStorage.getItem('privacy:locationConsent');
  const ts = localStorage.getItem('privacy:locationConsent:ts');

  const isFresh =
    ts && Date.now() - Number(ts) < 1000 * 60 * 30; // 30 min cache

  if (cached === 'true' && isFresh) return true;

  try {
    const { data } = await api.get('/privacy/preferences');
    const ok = !!data?.locationConsent;
    localStorage.setItem('privacy:locationConsent', String(ok));
    localStorage.setItem('privacy:locationConsent:ts', String(Date.now()));
    return ok;
  } catch {
    return false;
  }
}


export const useLocationHandling = () => {
  const { location, updateLocation } = useLocationContext();

  /** Ask device for location only if consent is ON. Returns the updated location from context. */
  const requestMyLocation = async () => {
    const consent = await getLocationConsent();
    if (!consent) {
      throw new Error('LOCATION_CONSENT_OFF');
    }
    // This triggers the context to use device geolocation internally
    // (your updateLocation implementation supports (undefined, undefined, true))
    const updated = await updateLocation(undefined, undefined, true);
    return updated as TemporaryMapLocation;
  };

  /** One-time init (optional): only tries device geolocate if consent is ON and no stored location. */
  const initializeLocation = async () => {
    const stored = sessionStorage.getItem('selectedLocation');

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.city && parsed?.country &&
            parsed.city !== 'Unknown' && parsed.country !== 'Unknown') {
          return;
        }
      } catch {}
    }

    if (location.city !== 'Unknown' && location.country !== 'Unknown') return;

    const consent = await getLocationConsent();
    if (!consent) {
      console.log('🔒 Location consent OFF — skipping device geolocation.');
      return;
    }

    try {
      await updateLocation(undefined, undefined, true);
    } catch (err) {
      console.error('❌ Location init failed:', err);
    }
  };

  const typedLocation = location as TemporaryMapLocation;
  return { location: typedLocation, updateLocation, initializeLocation, requestMyLocation };
};
